import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from 'material-react-table';
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import Box from "@mui/material/Box";
import {Button, Stack, Tooltip, Typography} from "@mui/material";
import {Cached, Delete} from "@mui/icons-material";
import MaterialEstimateUnitService
    from "../../../../../../API/orders/estimate/organization/MaterialEstimateUnitService";
import ModalConfirm from "../../../../modal/ModalConfirm";
import EstimateService from "../../../../../../API/orders/estimate/organization/EstimateService";
import {useNavigate} from "react-router-dom";
import ModalWidthCheckbox from "../../../../modal/ModalWidthCheckbox";
import {
    ESTIMATE_GLOBAL_FILTER_FN,
    estimateFilterFns,
    estimateGetColumnCanGlobalFilter,
    filterEstimateRowsByGlobalSearch,
    getMaterialEstimateArticle,
    getMaterialEstimateName,
    getMaterialEstimateUnitStateLabel,
    sortMaterialEstimateByCatalogArticle,
    sortMaterialEstimateByCatalogName,
} from "../../../../../../utils/orders/estimateTableFilter";
import DetailUtil from "../../../../../../utils/cutting/DetailUtil";

const initColumn = {
    id: false,
    basePrice: true,
    baseJobPrice: true,
    baseTotalPrice: true,
    calculationCostBasePrice: true,
    costBasePrice: true,
    calculationCostBaseJobPrice: true,
    costBaseJobPrice: true,
    calculationTotalCostBasePrice: true,
    totalCostBasePrice: true,
    profitCalculationBaseMaterialPrice: true,
    profitBaseMaterialPrice: true,
    profitCalculationBaseJobPrice: true,
    profitBaseJobPrice: true,
    profitCalculationBaseTotalPrice: true,
    profitBaseTotalPrice: true,
    alternativePrice: false,
    alternativeJobPrice: false,
    alternativeTotalPrice: false,
    calculationCostAlternativePrice: false,
    costAlternativePrice: false,
    calculationCostAlternativeJobPrice: false,
    costAlternativeJobPrice: false,
    calculationTotalCostAlternativePrice: false,
    totalCostAlternativePrice: false,
    profitCalculationAlternativeMaterialPrice: false,
    profitAlternativeMaterialPrice: false,
    profitCalculationAlternativeJobPrice: false,
    profitAlternativeJobPrice: false,
    profitCalculationAlternativeTotalPrice: false,
    profitAlternativeTotalPrice: false,
}
const CalculationTable = ({
                              data,
                              setData,
                              currency,
                              currentCurrency,
                              estimateId,
                              orderId,
                              orderNumber,
                              orderHeaderId,
                              year,
                              isButtonPurchase,
                              isActual,
                              enableEditing,
                              inWork,
                              isFact,
                              detailed,
                              tableOptions,
                              tableOptionsHandle,
                              errorIds,
                              handleModalAlert
                          }) => {
        const [loading, setLoading] = useState(false)
        const [currentEditRowData, setCurrentEditRowData] = useState(null);
        const [materialPriceUnits, setMaterialPriceUnits] = useState(new Map());
        const [columnVisibility, setColumnVisibility] = useState(initColumn)

        const [setPurchaseModalOpen, setSetPurchaseModalOpen] = useState(false);

        const [currentInWork, setCurrentInWork] = useState(inWork);

        const [searchSimilarMaterials, setSearchSimilarMaterials] = useState(false);
        const [globalFilter, setGlobalFilter] = useState('');

        const filteredData = useMemo(
            () => filterEstimateRowsByGlobalSearch(data, globalFilter),
            [data, globalFilter],
        );

        useEffect(() => {
            if (currentCurrency === 1) {
                setColumnVisibility(initColumn)
            } else {
                const newColumn = Object.entries(initColumn).map(el => el[0] === "id" ? el : [el[0], !el[1]])
                setColumnVisibility(Object.fromEntries(newColumn))
            }
        }, [currentCurrency]);
        const navigate = useNavigate();

        const redirectEstimateItem = () => {
            navigate('/organization/orders/' + orderId + '/' + encodeURIComponent(orderNumber) + '/' + orderHeaderId + '/' + year + '/estimate');
        };

        const calcSum = (type) => {
            return data.reduce((acc, mEU) => {
                if (mEU[type] !== null && mEU[type] !== 0) {
                    return acc + mEU[type].price;
                } else {
                    return acc;
                }
            }, 0)?.toLocaleString?.('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            })
        }

        const averageGrossMargin = useMemo(
            () => {
                if (data.length === 0) {
                    return 0;
                }
                const totalGrossMargin = data.reduce((acc, mEU) => acc + mEU.grossMargin, 0);
                return totalGrossMargin / data.length;
            },
            [data]
        );

        const handleDeleteRow = useCallback(
            (row) => {
                const id = row.original.id;
                const materialId = row.original.id
                MaterialEstimateUnitService.delete(id)
                    .then(function () {
                        data.splice(row.index, 1);
                        setData([...data]);
                        materialPriceUnits.delete(materialId);
                    })
                    .catch(function (error) {
                        handleModalAlert(error);
                    })
            },
            [data],
        );

        const handleSubmitSetPurchase = async (ignoreSlab, handleClose) => {
            setLoading(true)
            EstimateService.setPurchase(orderId, [], ignoreSlab)
                .then(function () {
                    handleClose()
                    setCurrentInWork(false);
                })
                .catch(function (error) {
                    handleModalAlert(error);
                })
                .finally(() => setLoading(false))
        }

        const handleCloseSetPurchase = () => {
            setSetPurchaseModalOpen(false);
        };

        const openSearchSimilarMaterials = useCallback(
            (row) => {
                setCurrentEditRowData(row.original);
                setSearchSimilarMaterials(true)
            },
            [],
        );

        const columns = useMemo(() => {
            const baseColumns = [

                {
                    id: 'articleNumber',
                    header: 'Артикул',
                    accessorFn: getMaterialEstimateArticle,
                    sortingFn: sortMaterialEstimateByCatalogArticle,
                    minSize: 15,
                    maxSize: 25,
                    Cell: ({row, renderedCellValue}) => {
                        const temp = !row.original.materialOrganizationOption?.articleNumber
                        return (
                            <span
                                style={{
                                    color: temp ? 'red' : 'black'
                                }}>
                               {renderedCellValue}
                            </span>
                        );
                    }
                },
                {
                    id: 'name',
                    header: 'Наименование',
                    accessorFn: getMaterialEstimateName,
                    sortingFn: sortMaterialEstimateByCatalogName,
                    minSize: 50,
                    maxSize: 50,
                    enableEditing: false,
                    Cell: ({row, renderedCellValue}) => {
                        const temp = row.original.materialOrganizationOption === null;
                        return (
                            <span
                                style={{
                                    color: temp ? 'red' : 'black'
                                }}>
                               {renderedCellValue}
                            </span>
                        );
                    }
                },
                {
                    id: 'unit',
                    header: 'Ед. изм.',
                    accessorFn: (row) => {
                        if (row.materialOrganizationOption !== null) {
                            return row.unit ? row.unit.name : 'Нет данных';
                        } else {
                            return row.tempUnit ? row.tempUnit.name : 'Нет данных';
                        }
                    },
                    minSize: 5,
                    maxSize: 5,
                },
                {
                    id: 'quantity',
                    header: 'Кол.',
                    accessorKey: 'quantity',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return (row.original.materialOrganizationOption === null) ?
                            '' : row.original.materialOrganizationOption.typeMaterial === 'SLAB' ?
                                (row.original.optimizationQuantity === null || row.original.optimizationQuantity === undefined
                                    ? row.original.quantity : row.original.optimizationQuantity.toFixed(3)) : value.toFixed(3);
                    },
                },
                {
                    id: 'basePrice',
                    header: `Материал`,
                    accessorKey: 'basePrice',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                    },
                    sortingFn: (rowA, rowB) => {
                        return Number(rowA.original.basePrice?.price) > Number(rowB.original.basePrice?.price) ? 1 : -1
                    },
                    Footer: () => (
                        <Stack>
                            <Box color="warning.main">
                                {calcSum("basePrice")} {currency[0]?.value}
                            </Box>
                        </Stack>
                    ),
                },
                {
                    id: 'alternativePrice',
                    header: `Материал`,
                    accessorKey: 'alternativePrice',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                    },
                    sortingFn: (rowA, rowB) => {
                        return Number(rowA.original.alternativePrice.price) > Number(rowB.original.alternativePrice.price) ? 1 : -1
                    },
                    Footer: () => (
                        <Stack>
                            <Box color="warning.main">
                                {calcSum("alternativePrice")} {currency[1]?.value}
                            </Box>
                        </Stack>
                    ),
                },
                {
                    id: 'baseJobPrice',
                    header: `Работа`,
                    accessorKey: 'baseJobPrice',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return value === null || value === undefined ? '-' : value.price.toFixed(2)
                    },
                    sortingFn: (rowA, rowB) => {
                        return Number(rowA.original.baseJobPrice?.price) > Number(rowB.original.baseJobPrice?.price) ? 1 : -1
                    },
                    Footer: () => (
                        <Stack>
                            <Box color="warning.main">
                                {calcSum("baseJobPrice")} {currency[0]?.value}
                            </Box>
                        </Stack>
                    ),
                },
                {
                    id: 'baseTotalPrice',
                    header: `Итого:`,
                    accessorKey: 'baseTotalPrice',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                    },
                    enableSorting: true,
                    sortingFn: (rowA, rowB) => {
                        return Number(rowA.original.baseTotalPrice.price) > Number(rowB.original.baseTotalPrice.price) ? 1 : -1
                    },
                    Footer: () => (
                        <Stack>
                            <Box color="warning.main">
                                {calcSum("baseTotalPrice")} {currency[0]?.value}
                            </Box>
                        </Stack>
                    ),
                },

                {
                    id: 'alternativeJobPrice',
                    header: `Работа`,
                    accessorKey: 'alternativeJobPrice',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return value === null || value === undefined ? '-' : value.price.toFixed(2)
                    },
                    sortingFn: (rowA, rowB) => {
                        return Number(rowA.original.alternativeJobPrice?.price) > Number(rowB.original.alternativeJobPrice?.price) ? 1 : -1
                    },
                    Footer: () => (
                        <Stack>
                            <Box color="warning.main">
                                {calcSum("alternativeJobPrice")} {currency[1]?.value}
                            </Box>
                        </Stack>
                    ),
                },
                {
                    id: 'alternativeTotalPrice',
                    header: `Итого:`,
                    accessorKey: 'alternativeTotalPrice',
                    minSize: 15,
                    maxSize: 15,
                    Cell: ({cell, row}) => {
                        const value = cell.getValue();
                        return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                    },
                    Footer: () => (
                        <Stack>
                            <Box color="warning.main">
                                {calcSum("alternativeTotalPrice")} {currency[1]?.value}
                            </Box>
                        </Stack>
                    ),
                },
                {
                    header: 'Статус',
                    id: 'materialEstimateUnitState',
                    accessorFn: getMaterialEstimateUnitStateLabel,
                    size: 150,
                    filterFn: (row, id, filterValue) => {
                        return getMaterialEstimateUnitStateLabel(row.original)
                            .toLowerCase()
                            .includes((filterValue ?? '').toString().toLowerCase())
                    },
                },
                {
                    header: 'Заметки',
                    accessorKey: 'note',
                    size: 25,
                },
                {
                    header: 'id',
                    accessorKey: 'id',
                    enableColumnActions: false,
                    enableGrouping: false,
                    enableEditing: false,
                    size: 2,
                },
            ];


            baseColumns.splice(4, 0, {
                id: 'costBasePrice',
                header: `СБ мат.`,
                accessorKey: 'costBasePrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.costBasePrice.price) > Number(rowB.original.costBasePrice.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("costBasePrice")} {currency[0]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(5, 0, {
                id: 'costBaseJobPrice',
                header: `СБ раб.`,
                accessorKey: 'costBaseJobPrice',
                minSize: 15,
                maxSize: 15,
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.costBaseJobPrice?.price) > Number(rowB.original.costBaseJobPrice?.price) ? 1 : -1
                },
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? '-' : value.price.toFixed(2)
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("costBaseJobPrice")} {currency[0]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(6, 0, {
                id: 'totalCostBasePrice',
                header: `СБ итого:`,
                accessorKey: 'totalCostBasePrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.totalCostBasePrice.price) > Number(rowB.original.totalCostBasePrice.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("totalCostBasePrice")} {currency[0]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(10, 0, {
                id: 'profitBaseMaterialPrice',
                header: `Прибыль мат.`,
                accessorKey: 'profitBaseMaterialPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.profitBaseMaterialPrice?.price) > Number(rowB.original.profitBaseMaterialPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("profitBaseMaterialPrice")} {currency[0]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(11, 0, {
                id: 'profitBaseJobPrice',
                header: `Прибыль раб.`,
                accessorKey: 'profitBaseJobPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? '-' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.profitBaseJobPrice?.price) > Number(rowB.original.profitBaseJobPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("profitBaseJobPrice")} {currency[0]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(12, 0, {
                id: 'profitBaseTotalPrice',
                header: `Прибыль итого:`,
                accessorKey: 'profitBaseTotalPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.profitBaseTotalPrice?.price) > Number(rowB.original.profitBaseTotalPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("profitBaseTotalPrice")} {currency[0]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(4, 0, {
                id: 'costAlternativePrice',
                header: `СБ мат.`,
                accessorKey: 'costAlternativePrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.costAlternativePrice?.price) > Number(rowB.original.costAlternativePrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("costAlternativePrice")} {currency[1]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(5, 0, {
                id: 'costAlternativeJobPrice',
                header: `СБ раб.`,
                accessorKey: 'costAlternativeJobPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? '-' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.costAlternativeJobPrice?.price) > Number(rowB.original.costAlternativeJobPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("costAlternativeJobPrice")} {currency[1]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(6, 0, {
                id: 'totalCostAlternativePrice',
                header: `СБ итого:`,
                accessorKey: 'totalCostAlternativePrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.totalCostAlternativePrice?.price) > Number(rowB.original.totalCostAlternativePrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("totalCostAlternativePrice")} {currency[1]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(18, 0, {
                id: 'profitAlternativeMaterialPrice',
                header: `Прибыль мат.`,
                accessorKey: 'profitAlternativeMaterialPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.profitAlternativeMaterialPrice?.price) > Number(rowB.original.profitAlternativeMaterialPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("profitAlternativeMaterialPrice")} {currency[1]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(19, 0, {
                id: 'profitAlternativeJobPrice',
                header: `Прибыль раб.`,
                accessorKey: 'profitAlternativeJobPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? '-' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.profitAlternativeJobPrice?.price) > Number(rowB.original.profitAlternativeJobPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("profitAlternativeJobPrice")} {currency[1]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(20, 0, {
                id: 'profitAlternativeTotalPrice',
                header: `Прибыль итого:`,
                accessorKey: 'profitAlternativeTotalPrice',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.price.toFixed(2)
                },
                sortingFn: (rowA, rowB) => {
                    return Number(rowA.original.profitAlternativeTotalPrice?.price) > Number(rowB.original.profitAlternativeTotalPrice?.price) ? 1 : -1
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {calcSum("profitAlternativeTotalPrice")} {currency[1]?.value}
                        </Box>
                    </Stack>
                ),
            });

            baseColumns.splice(22, 0, {
                id: 'grossMargin',
                header: `Валовая маржа`,
                accessorKey: 'grossMargin',
                minSize: 15,
                maxSize: 15,
                Cell: ({cell, row}) => {
                    const value = cell.getValue();
                    return value === null || value === undefined ? 'Ожидание' : value.toFixed(2)
                },
                Footer: () => (
                    <Stack>
                        <Box color="warning.main">
                            {"СР"} {averageGrossMargin?.toLocaleString?.('en-US', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                        })}
                        </Box>
                    </Stack>
                ),
            });

            return baseColumns;
        }, [data, setData]);

        return (
            <div>
                <Box
                    sx={{display: 'flex', justifyContent: "flex-end", gap: "10px", paddingBottom: "30px"}}
                >
                    {(isFact && errorIds?.isFact?.length > 0 || !isFact && errorIds?.isCalc?.length > 0) && <Button
                        size="small"
                        color="secondary"
                        variant="contained"
                        sx={{
                            animation: 'blinking 1s infinite',
                            WebkitAnimation: 'blinking 1s infinite',
                            '@keyframes blinking': {
                                '0%': {backgroundColor: '#fff'},
                                '50%': {backgroundColor: '#f00'},
                                '100%': {backgroundColor: '#fff'},
                            },
                        }}
                    >
                        Наименования одинаковые
                    </Button>}
                </Box>
                <MaterialReactTable
                    renderTopToolbar={({table}) => {
                        return <Box sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "7px",
                            backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0 ? theme.palette.primary.light : "transparent",
                        }}>
                            <MRT_ToolbarAlertBanner table={table}/>
                            <Box sx={{display: "flex"}}>
                                <MRT_ToggleFiltersButton table={table}/>
                                <MRT_GlobalFilterTextField table={table}/>
                            </Box>
                        </Box>
                    }}
                    selectAllMode={"all"}
                    muiTableBodyCellProps={({cell, table}) => {
                        const errorRow = DetailUtil.isDuplicateEstimateRow(errorIds, isFact, cell.row.original.id)
                        return ({
                            sx: {
                                p: '0px 0px 0px 10px',
                                m: '0px 0px 0px 0px',
                                border: "none",
                                backgroundColor: errorRow ? "red !important" : undefined,
                                "&:after": {
                                    display: "none",
                                },
                                "& td:after": {
                                    "&:hover": {
                                        backgroundColor: "red"
                                    }
                                },
                                "&:not(:last-of-type)": {
                                    borderRight: '1px solid #EAEBEA',

                                },
                                "& li": {
                                    listStyleType: "none",
                                    "& a": {
                                        color: errorRow ? "white" : "#006CCC",
                                        textDecoration: "none",
                                        "&:hover": {
                                            textDecoration: "underline"
                                        }
                                    }
                                }
                            },
                        })
                    }}
                    muiTableHeadCellProps={{
                        sx: {
                            border: '1px solid #EAEBEA',
                            backgroundColor: "#F5F6FA",
                            padding: "10px",
                            paddingBottom: " 0.2rem",
                            paddingTop: "0.2rem"
                        },
                    }}
                    muiTableContainerProps={{
                        sx: {
                            maxHeight: `calc(100vh - 600px)`,
                        }
                    }}
                    muiTableBodyRowProps={({row}) => {
                        const errorRow = DetailUtil.isDuplicateEstimateRow(errorIds, isFact, row.original.id)
                        return ({
                            sx: (theme) => ({
                                height: '36px',
                                backgroundColor: errorRow ? "red" : "white",
                                "& td": errorRow ? {backgroundColor: "red !important"} : undefined,
                                "&:hover& td:after": {
                                    display: "none",
                                },
                                "&.MuiTableRow-root.Mui-selected": {
                                    backgroundColor: errorRow ? "red" : theme.palette.primary.light70,
                                    "&:hover": {
                                        backgroundColor: errorRow ? "red" : theme.palette.primary.light50,
                                    },
                                },
                                "&:hover": {
                                    "& td:after": {
                                        display: "none",
                                    },
                                    backgroundColor: errorRow ? "red" : theme.palette.primary.stroke,
                                    ...(errorRow ? {"& td": {backgroundColor: "red !important"}} : {}),
                                },
                                '&:nth-of-type(even)': {
                                    "&.MuiTableRow-root.Mui-selected": {
                                        backgroundColor: errorRow ? "red" : theme.palette.primary.light,
                                        "&:hover": {
                                            backgroundColor: errorRow ? "red" : theme.palette.primary.light50,
                                        },
                                    },
                                    backgroundColor: errorRow ? "red" : theme.palette.primary.lightGrey,
                                    "&:hover": {
                                        backgroundColor: errorRow ? "red" : theme.palette.primary.stroke,
                                    },
                                    ...(errorRow ? {"& td": {backgroundColor: "red !important"}} : {}),
                                },
                            })
                        })
                    }}

                    displayColumnDefOptions={{
                        'mrt-row-actions': {
                            header: "",
                            size: 10,
                            grow: false,
                        },
                        'mrt-row-select': {
                            size: 10,
                            grow: false,
                        },
                    }}
                    muiSearchTextFieldProps={{
                        placeholder: `Поиск`,
                        sx: {
                            paddingTop: "5px",
                            paddingBottom: "0px",
                            "& .MuiCollapse-root": {
                                borderRadius: '12px',
                                height: 40, width: 290,
                                "& .MuiSvgIcon-root": {
                                    color: "#737478"
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: 0
                                }
                            }
                        },
                        variant: 'outlined',
                    }}
                    columns={columns}
                    data={filteredData}
                    rowHeight={13}
                    columnSpacing={1}
                    onGlobalFilterChange={setGlobalFilter}
                    globalFilterFn={ESTIMATE_GLOBAL_FILTER_FN}
                    filterFns={estimateFilterFns}
                    getColumnCanGlobalFilter={estimateGetColumnCanGlobalFilter}
                    enableGlobalFilterRankedResults={false}
                    initialState={{
                        columnVisibility: initColumn,
                        isFullScreen: false,
                        density: "compact",
                        pagination: {pageIndex: 0, pageSize: 100},
                        showGlobalFilter: true,
                    }}
                    rowCount={filteredData.length}
                    enableEditing={false}
                    onPaginationChange={(updater) => {
                        tableOptionsHandle(updater(tableOptions));
                    }}
                    state={{columnVisibility, showGlobalFilter: true, pagination: tableOptions, globalFilter}}
                    getRowId={(originalRow) => originalRow.id}
                    selectionPadding={1}
                    selectionCss={{
                        backgroundColor: 'lightblue',
                        borderWidth: '2px',
                        borderStyle: 'solid',
                        borderColor: 'blue',
                        borderRadius: '4px',
                    }}
                    renderRowActions={({row, table}) => (
                        <Box sx={{
                            display: 'flex',
                            gap: '0.1rem',
                            justifyContent: 'flex-start',
                            maxWidth: '50px'
                        }}>
                            {!row.original.isBlocked ?
                                <Tooltip arrow placement="left" title="Заменить">
                                    <Button size="small" sx={{minWidth: 'auto', marginLeft: 'auto'}} color="pencil"
                                            onClick={() => openSearchSimilarMaterials(row)}>
                                        <Cached sx={{minWidth: 'auto', marginLeft: 'auto', fontSize: 20}}/>
                                    </Button>
                                </Tooltip> : <Button size="small" sx={{minWidth: 'auto', marginLeft: 'auto'}} color="pencil"
                                                     onClick={() => openSearchSimilarMaterials(row)}>
                                    <Cached sx={{minWidth: 'auto', marginLeft: 'auto', fontSize: 20}}/>
                                </Button>
                            }
                            {!row.original.isBlocked ? <Tooltip arrow placement="right" title="Удалить">
                                <Button
                                    size="small"
                                    sx={{minWidth: 'auto', marginLeft: 'auto'}}
                                    color="error"
                                    onClick={() => handleDeleteRow(row)}
                                >
                                    <Delete sx={{minWidth: 'auto', marginLeft: 'auto', fontSize: 20}}/>
                                </Button>
                            </Tooltip> : <Button
                                size="small"
                                sx={{minWidth: 'auto', marginLeft: 'auto'}}
                                color="error"
                                onClick={() => handleDeleteRow(row)}
                                disabled={true}
                            >
                                <Delete sx={{minWidth: 'auto', marginLeft: 'auto', fontSize: 20}}/>
                            </Button>}
                        </Box>
                    )}
                    localization={MRT_Localization_RU}
                    enableToolbarInternalActions={false}
                    renderTopToolbarCustomActions={({table}) => {
                        return (
                            <div style={{display: "flex"}}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        p: '0.5rem',
                                        flexWrap: 'wrap'
                                    }}
                                >
                                    {detailed && <Button
                                        size="small"
                                        variant="outlined"
                                        style={{color: "black", borderColor: "black", fontWeight: "bold", fontSize: 14}}
                                        onClick={() => redirectEstimateItem()}
                                    >
                                        Подробно
                                    </Button>}
                                </Box>
                            </div>
                        );
                    }}
                />
                <ModalWidthCheckbox
                    open={setPurchaseModalOpen}
                    onClose={handleCloseSetPurchase}
                    handleSubmit={(ignoreSlab, handleClose) => handleSubmitSetPurchase(ignoreSlab, handleClose)}
                    title={"Подтвердить создание заявки на закупку?"}
                    action={"Подтвердить"}
                    loading={loading}/>
            </div>
        )
            ;
    }
;

export default CalculationTable;
